import { State, Action, StateContext, Selector, Store } from '@ngxs/store';
import { Injectable } from '@angular/core';
import { Alert } from 'app/models/dto/alert.model';
import { ClearDealAlertsStore, CreateNewDealAlert, DismissDealAlert, DismissDealError, GetDealErrors } from './deal-alerts.actions';
import { DealsService } from 'app/services/deals/deals.service';
import { map, tap } from 'rxjs/operators';

export class DealAlertsStateModel {
  dealAlerts: {
    dealId: number;
    alerts: Alert[];
  }[];
}

@State<DealAlertsStateModel>({
  name: 'dealAlerts',
  defaults: {
    dealAlerts: []
  }
})
@Injectable()

export class DealAlertsState {

  @Selector()
  static dealAlerts(state: DealAlertsStateModel) {
    return (dealId: number) => {
      const dealAlerts = state.dealAlerts.filter(alerts => alerts.dealId === dealId);
      return dealAlerts.length ? dealAlerts[0].alerts : [];
    };
  }

  constructor(
    private dealsService: DealsService,
    private store: Store
  ) {}

  @Action(GetDealErrors)
	getDealErrors({}: StateContext<DealAlertsStateModel>, { borrowerId }: GetDealErrors) {
    return this.dealsService.getDealErrors(borrowerId).pipe(map(errors => {
      errors.forEach(error => {
        const newAlert: Alert = {
          level: 'error',
          message: error.errorMessage,
          errorId: error.id
        };
        this.store.dispatch(new CreateNewDealAlert(error.dealId, newAlert));
      });
    }));
  }

  @Action(CreateNewDealAlert)
  createNewDealAlert({ getState, patchState }: StateContext<DealAlertsStateModel>, { dealId, alert }: CreateNewDealAlert) {
    const dealAlertsStateCopy = [ ...getState().dealAlerts ];
    const dealAlertsIndex = getState().dealAlerts.findIndex(a => a.dealId === dealId);
    if (dealAlertsIndex !== -1) {
      const dealAlertsCopy = { ...getState().dealAlerts[dealAlertsIndex] };
      dealAlertsCopy.alerts.unshift(alert);
      dealAlertsStateCopy[dealAlertsIndex] = dealAlertsCopy;
      patchState({ dealAlerts: dealAlertsStateCopy });
    } else {
      patchState({
        dealAlerts: [
          ...getState().dealAlerts,
          { dealId: dealId, alerts: [alert] }
        ]
      });
    }
  }


  @Action(DismissDealError)
	dismissDealError({ getState, patchState }: StateContext<DealAlertsStateModel>, { errorId, dealId }: DismissDealError) {
    return this.dealsService.dismissDealError(errorId).pipe(tap(() => {
      const dealAlertsStateCopy = [ ...getState().dealAlerts ];
      const dealAlertsIndex = getState().dealAlerts.findIndex(a => a.dealId === dealId);
      const dealAlertsCopy = { ...getState().dealAlerts[dealAlertsIndex] };
      const updatedAlerts = dealAlertsCopy.alerts.filter(a => !a.errorId || a.errorId !== errorId);
      dealAlertsCopy.alerts = updatedAlerts;
      dealAlertsStateCopy[dealAlertsIndex] = dealAlertsCopy;
      patchState({ dealAlerts: dealAlertsStateCopy });
    }));
  }

  @Action(DismissDealAlert)
  dismissDealAlert({ getState, patchState }: StateContext<DealAlertsStateModel>, { dealId, alert }: DismissDealAlert) {
    if (alert.errorId) {
      this.store.dispatch(new DismissDealError(alert.errorId, dealId));
    } else {
      const dealAlertsStateCopy = [ ...getState().dealAlerts ];
      const dealAlertsIndex = getState().dealAlerts.findIndex(a => a.dealId === dealId);
      const dealAlertsCopy = { ...getState().dealAlerts[dealAlertsIndex] };
      const updatedAlerts = dealAlertsCopy.alerts.filter(a => a.level !== alert.level && a.message !== alert.message);
      dealAlertsCopy.alerts = updatedAlerts;
      dealAlertsStateCopy[dealAlertsIndex] = dealAlertsCopy;
      patchState({ dealAlerts: dealAlertsStateCopy });
    }
  }

  @Action(ClearDealAlertsStore)
  clearDealAlertsStore({ patchState }: StateContext<DealAlertsStateModel>, {}: ClearDealAlertsStore) {
    patchState({ dealAlerts: [] });
  }
}
